import { courtDimensions, courtViewport, MARKER_CLEARANCE } from './courtGeometry'

export type Point = { x: number; y: number }
export type TeamSide = 'offense' | 'defense'
export type Tool = 'select' | 'marker' | 'line' | 'eraser' | 'animate'
export type Alignment = 'stack' | 'one-four-high' | 'box' | 'line-stack'
export type AlignmentPreset = { id: string; label: string; positions: Point[]; builtIn?: boolean }
export type PlayerLayoutAction = 'align-horizontal' | 'align-vertical' | 'distribute-horizontal' | 'distribute-vertical'

export type PlayerMarker = Point & {
  id: string
  label: string
  side: TeamSide
}

export type Stroke = {
  id: string
  points: Point[]
  kind: 'ink' | 'arrow'
  color: string
  style?: 'cut' | 'pass' | 'screen' | 'dribble'
}

export type BoardState = {
  id: string
  title: string
  players: PlayerMarker[]
  ballHandlerId: string | null
  strokes: Stroke[]
  flipped: boolean
  showDefense: boolean
  fullCourt?: boolean
  updatedAt: number
  play?: import('./play').Play
  looseBall?: Point
  familyId?: string
  favorite?: boolean
  assignments?: { teamId: string; alias: string; notes: string; category: string }[]
  whiteboardPhase?: 'setup' | 'drawing'
}

const offense: PlayerMarker[] = [
  { id: 'o1', label: '1', side: 'offense', x: 0.5, y: 0.72 },
  { id: 'o2', label: '2', side: 'offense', x: 0.14, y: 0.54 },
  { id: 'o3', label: '3', side: 'offense', x: 0.86, y: 0.54 },
  { id: 'o4', label: '4', side: 'offense', x: 0.08, y: 0.18 },
  { id: 'o5', label: '5', side: 'offense', x: 0.92, y: 0.18 },
]

const defense: PlayerMarker[] = offense.map((player, index) => ({
  ...player,
  id: `d${index + 1}`,
  label: `${index + 1}`,
  side: 'defense',
  y: Math.max(0.1, player.y - 0.075),
}))

export const alignments: { id: Alignment; label: string }[] = [
  { id: 'stack', label: 'Stack' },
  { id: 'one-four-high', label: '1–4 High' },
  { id: 'box', label: 'Box' },
  { id: 'line-stack', label: 'Line stack' },
]

const alignmentPositions: Record<Alignment, Point[]> = {
  // The first position is always the point guard / inbounder so the ball and
  // player labels stay attached when an alignment is applied.
  stack: [
    { x: .18, y: .84 }, { x: .44, y: .20 }, { x: .44, y: .31 },
    { x: .44, y: .42 }, { x: .44, y: .53 },
  ],
  'one-four-high': [
    { x: .5, y: .73 }, { x: .16, y: .39 }, { x: .84, y: .39 },
    { x: .39, y: .39 }, { x: .61, y: .39 },
  ],
  box: [
    { x: .5, y: .76 }, { x: .28, y: .20 }, { x: .72, y: .20 },
    { x: .28, y: .43 }, { x: .72, y: .43 },
  ],
  'line-stack': [
    { x: .16, y: .78 }, { x: .27, y: .34 }, { x: .42, y: .34 },
    { x: .58, y: .34 }, { x: .73, y: .34 },
  ],
}

export const defaultAlignmentPresets: AlignmentPreset[] = alignments.map(alignment => ({
  ...alignment,
  positions: alignmentPositions[alignment.id].map(point => ({ ...point })),
  builtIn: true,
}))

export function mergeAlignmentPresets(saved: AlignmentPreset[]): AlignmentPreset[] {
  const overrides = new Map(saved.map(preset => [preset.id, preset]))
  const builtIns = defaultAlignmentPresets.map(preset => {
    const override = overrides.get(preset.id)
    return override ? { ...override, positions: override.positions.map(point => ({ ...point })), builtIn: true } : structuredClone(preset)
  })
  const custom = saved.filter(preset => !defaultAlignmentPresets.some(item => item.id === preset.id))
    .map(preset => ({ ...preset, positions: preset.positions.map(point => ({ ...point })), builtIn: false }))
  return [...builtIns, ...custom]
}

export function captureAlignment(board: BoardState): Point[] {
  return board.players.filter(player => player.side === 'offense').map(({ x, y }) => ({ x, y }))
}

export function applyAlignment(board: BoardState, alignment: Alignment | AlignmentPreset): BoardState {
  const positions = typeof alignment === 'string' ? alignmentPositions[alignment] : alignment.positions
  if (positions.length !== 5) return board
  let offenseIndex = 0
  return {
    ...board,
    players: board.players.map(player => player.side === 'offense'
      ? { ...player, ...positions[offenseIndex++] }
      : player),
    updatedAt: Date.now(),
  }
}

export function swapPlayerLocations(board: BoardState, firstId: string, secondId: string): BoardState {
  if (firstId === secondId) return board
  const first = board.players.find(player => player.id === firstId)
  const second = board.players.find(player => player.id === secondId)
  if (!first || !second || first.side !== second.side) return board
  return {
    ...board,
    players: board.players.map(player => player.id === firstId
      ? { ...player, x: second.x, y: second.y }
      : player.id === secondId ? { ...player, x: first.x, y: first.y } : player),
    updatedAt: Date.now(),
  }
}

export function layoutPlayers(board: BoardState, playerIds: string[], action: PlayerLayoutAction): BoardState {
  const selected = board.players.filter(player => player.side === 'offense' && playerIds.includes(player.id))
  if (selected.length < 2 || (action.startsWith('distribute') && selected.length < 3)) return board
  const points = new Map<string, Point>()
  if (action === 'align-horizontal') {
    const y = selected.reduce((sum, player) => sum + player.y, 0) / selected.length
    selected.forEach(player => points.set(player.id, { x: player.x, y }))
  } else if (action === 'align-vertical') {
    const x = selected.reduce((sum, player) => sum + player.x, 0) / selected.length
    selected.forEach(player => points.set(player.id, { x, y: player.y }))
  } else {
    const axis = action === 'distribute-horizontal' ? 'x' : 'y'
    const sorted = [...selected].sort((a, b) => a[axis] - b[axis])
    const start = sorted[0][axis], end = sorted.at(-1)![axis], step = (end - start) / (sorted.length - 1)
    sorted.forEach((player, index) => points.set(player.id, { ...player, [axis]: start + step * index }))
  }
  return {
    ...board,
    players: board.players.map(player => points.has(player.id) ? { ...player, ...points.get(player.id)! } : player),
    updatedAt: Date.now(),
  }
}

export function createBoard(title = 'Untitled board'): BoardState {
  return {
    id: crypto.randomUUID(),
    title,
    players: [...offense.map((p) => ({ ...p })), ...defense.map((p) => ({ ...p }))],
    ballHandlerId: 'o1',
    strokes: [],
    flipped: false,
    showDefense: false,
    whiteboardPhase: 'setup',
    updatedAt: Date.now(),
  }
}

export function cloneBoard(board: BoardState): BoardState {
  return structuredClone(board)
}

export function displayPoint(point: Point, flipped: boolean): Point {
  return flipped ? { x: 1 - point.x, y: 1 - point.y } : point
}

export function modelPoint(point: Point, flipped: boolean): Point {
  return displayPoint(point, flipped)
}

export function clampPoint(point: Point, fullCourt = false, bounds = courtViewport(fullCourt)): Point {
  const size = courtDimensions(fullCourt), view = bounds
  return {
    x: Math.min((view.x+view.width-MARKER_CLEARANCE)/size.width, Math.max((view.x+MARKER_CLEARANCE)/size.width, point.x)),
    y: Math.min((view.y+view.height-MARKER_CLEARANCE)/size.height, Math.max((view.y+MARKER_CLEARANCE)/size.height, point.y)),
  }
}

export function boardsEqual(a: BoardState, b: BoardState): boolean {
  return JSON.stringify({ ...a, updatedAt: 0 }) === JSON.stringify({ ...b, updatedAt: 0 })
}

export function snapPoint(point: Point, board: BoardState): Point {
  const target = board.players.filter(p => p.side === 'offense' || board.showDefense)
    .find(p => Math.hypot(p.x - point.x, p.y - point.y) < .045)
  return target ? { x: target.x, y: target.y } : point
}
